import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { SbTabs, SbNotification } from "uiCore";
import { PageTransitionStyled } from "../talentMobility.animations";
import { TEXT } from "../talentMobility.constants";
import {
  getViewPlanningRedeploymentPlans,
  getViewPlanningRedeploymentPlanRoles,
  getViewPlanningWorkforcePlans,
  getViewPlanningWorkforcePlanJobs,
} from "Api/apiService";
import { VIEW_PLANNING_TAB } from "./ViewPlanningV2.constants";
import { buildRedeploymentRoleColumns, buildWorkforceJobColumns } from "./useViewPlanningColumns";
import { TabsBodyStyled } from "./ViewPlanningV2.styles";
import PlansListTable from "./components/PlansListTable";
import PlanDrillDownList from "./components/PlanDrillDownList";
import PlanRoleDetail from "./components/PlanRoleDetail";
import WorkforceJobDetail from "./components/WorkforceJobDetail";

import useAnalytics from "Hooks/useAnalytics";
import { CROSS_PAGE_TOUR_PHASE, getTourLegSteps } from "../TalentMobilityManagerV2/TalentMobilityManagerV2.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { ANALYTICS_EVENTS } from "Utils/analytics";



// Planning Tools ▸ View Planning — read-only view over the saved plans.
// Two tabs, both landing on their saved plans list (name, creator, date,
// job count): Redeployment Planning drills plan → roles → the role's
// Reduction Detail mirror; Workforce Planning drills plan → jobs → the
// job's available/unavailable candidates. This page owns the drill-down
// state and fetches; the components/ files own presentation.
const ANALYTICS_FROM = "talent_mobility_view_planning";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.VIEW_PLANNING_VIEWED;

const ViewPlanningV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  // ── Guided tour: this page's leg of the Talent Mobility tour ───────────────
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const tabsBodyRef = useRef(null);
  const handOffTour = (phase, route) => {
    setCrossPageTourPhase(phase);
    navigate(route);
  };
  const location = useLocation();
  // Seeded from router state (see persistDrillState below) so that
  // navigating away — e.g. into a Candidate Profile via a flow=view-planning
  // "View Profile" link — and back via the browser's Back button remounts
  // this page on the same tab/plan/role/job instead of always resetting to
  // the plans list.
  const [activeTab, setActiveTabRaw] = useState(location.state?.activeTab || VIEW_PLANNING_TAB.REDEPLOYMENT);
  const [plans, setPlans] = useState(null); // null = loading
  const [selectedPlan, setSelectedPlanRaw] = useState(location.state?.selectedPlan || null); // null = plans list view
  const [redeploymentRoles, setRedeploymentRoles] = useState(null); // null = loading
  const [selectedRole, setSelectedRoleRaw] = useState(location.state?.selectedRole || null); // null = plan roles view
  const [workforcePlans, setWorkforcePlans] = useState(null); // null = loading
  const [selectedWorkforcePlan, setSelectedWorkforcePlanRaw] = useState(location.state?.selectedWorkforcePlan || null); // null = plans list view
  const [workforceJobs, setWorkforceJobs] = useState(null); // null = loading
  const [selectedWorkforceJob, setSelectedWorkforceJobRaw] = useState(location.state?.selectedWorkforceJob || null); // null = plan jobs view

  // Replaces this page's own history entry with the drill-down state it's
  // moving to, not just component state. Component state alone doesn't
  // survive navigating away and back (React Router remounts this page fresh
  // on that route), but the history entry's state does — navigate(-1) from a
  // page this component pushed restores it here on mount, above. `patch`
  // overrides only the field(s) this call is changing; every other field
  // falls back to its current (already up to date) value.
  const persistDrillState = (patch) => {
    navigate(location.pathname, {
      replace: true,
      state: {
        activeTab,
        selectedPlan,
        selectedRole,
        selectedWorkforcePlan,
        selectedWorkforceJob,
        ...patch,
      },
    });
  };

  const setActiveTab = (nextTab) => {
    setActiveTabRaw(nextTab);
    persistDrillState({ activeTab: nextTab });
  };
  const setSelectedPlan = (nextPlan) => {
    setSelectedPlanRaw(nextPlan);
    persistDrillState({ selectedPlan: nextPlan });
  };
  const setSelectedRole = (nextRole) => {
    setSelectedRoleRaw(nextRole);
    persistDrillState({ selectedRole: nextRole });
  };
  const setSelectedWorkforcePlan = (nextPlan) => {
    setSelectedWorkforcePlanRaw(nextPlan);
    persistDrillState({ selectedWorkforcePlan: nextPlan });
  };
  const setSelectedWorkforceJob = (nextJob) => {
    setSelectedWorkforceJobRaw(nextJob);
    persistDrillState({ selectedWorkforceJob: nextJob });
  };

  // Fetched when the Redeployment Planning tab is active (it's the default,
  // so also on first load), once per visit.
  useEffect(() => {
    if (activeTab !== VIEW_PLANNING_TAB.REDEPLOYMENT || plans !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningRedeploymentPlans();
        if (!cancelled) setPlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (error) {
        if (cancelled) return;
        setPlans([]);
        SbNotification.error(
          "Unable to load redeployment plans",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, plans]);

  // The selected plan's role rows, fetched on drill-in.
  useEffect(() => {
    if (!selectedPlan) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningRedeploymentPlanRoles(selectedPlan.plan_id);
        if (!cancelled) setRedeploymentRoles(Array.isArray(response?.roles) ? response.roles : []);
      } catch (error) {
        if (cancelled) return;
        setRedeploymentRoles([]);
        SbNotification.error(
          "Unable to load redeployment roles",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPlan]);

  // Workforce plans, fetched the first time the Workforce Planning tab opens.
  useEffect(() => {
    if (activeTab !== VIEW_PLANNING_TAB.WORKFORCE || workforcePlans !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningWorkforcePlans();
        if (!cancelled) setWorkforcePlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (error) {
        if (cancelled) return;
        setWorkforcePlans([]);
        SbNotification.error(
          "Unable to load workforce plans",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, workforcePlans]);

  // The selected workforce plan's job rows, fetched on drill-in.
  useEffect(() => {
    if (!selectedWorkforcePlan) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningWorkforcePlanJobs(selectedWorkforcePlan.plan_id);
        if (!cancelled) setWorkforceJobs(Array.isArray(response?.jobs) ? response.jobs : []);
      } catch (error) {
        if (cancelled) return;
        setWorkforceJobs([]);
        SbNotification.error(
          "Unable to load workforce plan jobs",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWorkforcePlan]);

  const openPlan = (plan) => {
    setRedeploymentRoles(null);
    setSelectedRoleRaw(null);
    setSelectedPlanRaw(plan);
    persistDrillState({ selectedPlan: plan, selectedRole: null });
  };

  const openWorkforcePlan = (plan) => {
    setWorkforceJobs(null);
    setSelectedWorkforceJobRaw(null);
    setSelectedWorkforcePlanRaw(plan);
    persistDrillState({ selectedWorkforcePlan: plan, selectedWorkforceJob: null });
  };

  // Edit column on the Redeployment Planning plans list — jumps into the
  // Redeployment Center immediately, carrying which plan to load as router
  // state; the Center itself fetches that plan's details and shows its own
  // loading state while the fetch is in flight (see RedeploymentCenterV2's
  // loadPlanId effect), rather than making the user wait on this page first.
  const handleEditPlan = (plan) => {
    navigate("/talent-mobility/redeployment-center", {
      state: { loadPlanId: plan.plan_id, loadPlanName: plan.plan_name },
    });
  };

  // Edit column on the Workforce Planning plans list — same "navigate now,
  // load there" handoff as handleEditPlan above, into Workforce Planning
  // instead (see WorkforcePlanningV2's loadPlanId effect).
  const handleEditWorkforcePlan = (plan) => {
    navigate("/talent-mobility/workforce-planning", {
      state: { loadPlanId: plan.plan_id, loadPlanName: plan.plan_name },
    });
  };

  // Column arrays memoize on their row data — this page re-renders on every
  // drill-down/tab state change, and rebuilding the filter-option lists
  // (Set + sort per column) each time is wasted work that also hands antd a
  // new columns identity.
  const redeploymentRoleColumns = useMemo(
    () => buildRedeploymentRoleColumns(redeploymentRoles || []),
    [redeploymentRoles]
  );
  const workforceJobColumns = useMemo(
    () => buildWorkforceJobColumns(workforceJobs || []),
    [workforceJobs]
  );

  let redeploymentTabContent;
  if (selectedPlan && selectedRole) {
    redeploymentTabContent = (
      <PlanRoleDetail
        selectedPlan={selectedPlan}
        selectedRole={selectedRole}
        onBack={() => setSelectedRole(null)}
      />
    );
  } else if (selectedPlan) {
    redeploymentTabContent = (
      <PlanDrillDownList
        planName={selectedPlan.plan_name}
        columns={redeploymentRoleColumns}
        rows={redeploymentRoles}
        rowKey={(record, index) =>
          record.redeploy_plan_unique_id || `${record.plan_id}-${record.jd_sb_unique_id}` || index
        }
        emptyMessage="No roles in this plan."
        onSelect={setSelectedRole}
        onBack={() => setSelectedPlan(null)}
      />
    );
  } else {
    redeploymentTabContent = (
      <PlansListTable
        plans={plans}
        emptyMessage="No redeployment plans saved yet."
        onOpenPlan={openPlan}
        onEdit={handleEditPlan}
      />
    );
  }

  let workforceTabContent;
  if (selectedWorkforcePlan && selectedWorkforceJob) {
    workforceTabContent = (
      <WorkforceJobDetail
        selectedPlan={selectedWorkforcePlan}
        selectedJob={selectedWorkforceJob}
        onBack={() => setSelectedWorkforceJob(null)}
      />
    );
  } else if (selectedWorkforcePlan) {
    workforceTabContent = (
      <PlanDrillDownList
        planName={selectedWorkforcePlan.plan_name}
        columns={workforceJobColumns}
        rows={workforceJobs}
        rowKey={(record, index) =>
          record.workforce_user_plan_id || `${record.plan_id}-${record.jd_sb_unique_id}` || index
        }
        emptyMessage="No jobs in this plan."
        onSelect={setSelectedWorkforceJob}
        onBack={() => setSelectedWorkforcePlan(null)}
      />
    );
  } else {
    workforceTabContent = (
      <PlansListTable
        plans={workforcePlans}
        emptyMessage="No workforce plans saved yet."
        onOpenPlan={openWorkforcePlan}
        onEdit={handleEditWorkforcePlan}
      />
    );
  }

  const tabItems = [
    {
      key: VIEW_PLANNING_TAB.REDEPLOYMENT,
      label: "Redeployment Planning",
      children: redeploymentTabContent,
    },
    {
      key: VIEW_PLANNING_TAB.WORKFORCE,
      label: "Workforce Planning",
      children: workforceTabContent,
    },
  ];

  return (
    <PageTransitionStyled>
      <PageHeader
        title={TEXT.BTN_VIEW_PLANNING}
        onBack={() => navigate("/talent-mobility")}
        backLabel="Back to Talent Mobility"
      />

      <TabsBodyStyled ref={tabsBodyRef}>
        <SbTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </TabsBodyStyled>

      {/* This page's leg of the Talent Mobility tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.VIEW_PLANS}
        // Spotlights only the tab row, which renders straight away — each tab
        // fetches its own plans lazily, so waiting on them would never settle.
        ready
        steps={getTourLegSteps(CROSS_PAGE_TOUR_PHASE.VIEW_PLANS)}
        resolveTarget={() => tabsBodyRef.current?.querySelector(".ant-tabs-nav") ?? tabsBodyRef.current}
        onNext={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
        onClose={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
      />
    </PageTransitionStyled>
  );
};

export default ViewPlanningV2;
